import { useState, useEffect } from "react";
import { Heart, MessageCircle, Store, Crown, Car, Flag, MoreHorizontal, Trash2, Edit2, Share2, Plus, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { Post } from "@shared/schema";
import { InsiderStar } from "@/components/InsiderStar";
import { PostDetailModal } from "@/components/PostDetailModal";
import { LoginModal } from "@/components/LoginModal";
import { ReportModal } from "@/components/ReportModal";
import { ShareModal } from "@/components/ShareModal";
import { useAuth } from "@/lib/authContext";
import { PostComposerModal } from "@/components/PostComposerModal";
import { useLocation } from "wouter";
import { getAuthHeader } from "@/lib/authHelpers";
import { generateShopSlug, generateRouteSlug } from "@/lib/slugify";
import { useI18n } from "@/lib/i18n";
import { TranslatableText } from "@/components/TranslatableText";
import { getShopProfileUrl } from "@/lib/shopUrl";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { WishlistItemFormDialog } from "./WishlistItemFormDialog";
import { getToken } from "@/lib/authContext";

interface PostCardProps {
  post: any; // Allow FeedItem or Post
  onLikeChange?: () => void;
  authorOverride?: {
    name: string;
    avatarUrl?: string | null;
  };
}

export function PostCard({ post, onLikeChange, authorOverride }: PostCardProps) {
  const { user, isLoggedIn } = useAuth();
  const { t, locale } = useI18n();
  const [, setLocation] = useLocation();
  const [showDetail, setShowDetail] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(post.likesCount || 0);
  const [commentsCount, setCommentsCount] = useState(post.commentsCount || 0);
  const [showEditModal, setShowEditModal] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [wishlistModalOpen, setWishlistModalOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Sync state with props when post data changes (refetched by parent)
  useEffect(() => {
    setLikeCount(post.likesCount || 0);
    setCommentsCount(post.commentsCount || 0);
  }, [post.likesCount, post.commentsCount]);

  const addWishlistMutation = useMutation({
    mutationFn: async (payload: any) => {
      const token = await getToken();
      if (!token) throw new Error(t("postComposer.loginRequired"));

      const res = await fetch("/api/wishlist/items", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed to add item");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-wishlist"] });
      setWishlistModalOpen(false);
      toast({ title: t("itemWishlist.addedTitle"), description: t("itemWishlist.addedDesc") });
    },
    onError: (err: Error) => {
      toast({ variant: "destructive", title: t("common.error"), description: err.message });
    },
  });

  const uploadImage = async (base64: string): Promise<{ url: string; thumbnail: string }> => {
    const token = await getToken();
    if (!token) throw new Error("Login required");

    const res = await fetch("/api/upload", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ images: [base64], bucket: "wishlist-images" }),
    });
    if (!res.ok) throw new Error("Upload failed");
    const data = await res.json();
    return { url: data.urls[0], thumbnail: data.thumbnails?.[0] || data.urls[0] };
  };

  const images = (post.images && Array.isArray(post.images) ? post.images : (post.imageUrl ? [post.imageUrl] : [])).filter(Boolean) as string[];
  const mainImage = images[0] || post.imageUrl;
  const isOwnerPost = !!post.asShopId || post.type === 'shop_post';

  const displayAuthorName = authorOverride?.name || post.authorContent?.name || post.author?.name || post.author?.username || post.authorUsername;
  const displayAuthorAvatar = authorOverride ? authorOverride.avatarUrl : (post.authorAvatarUrl || post.authorContent?.avatar_url || post.author?.avatar_url);
  const createdAt = post.createdAt || post.created_at;
  const caption = post.caption || post.content;
  const category = post.category || (post.type === 'shop_post' ? t("shopUpdates.title") : "");

  // Check if user has liked this post (simplified - could load from API)
  // For now, we'll check on modal open

  function handleImageClick() {
    setShowDetail(true);
  }

  function handleCommentClick(e: React.MouseEvent) {
    e.stopPropagation();
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }
    setShowDetail(true);
  }

  async function handleLike(e: React.MouseEvent) {
    e.stopPropagation();

    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }

    try {
      const authHeader = await getAuthHeader();
      const response = await fetch(`/api/posts/${post.id}/like`, {
        method: "POST",
        headers: {
          ...(authHeader || {}),
        },
      });

      if (response.ok) {
        const data = await response.json();
        setLiked(data.liked);
        setLikeCount(data.likeCount);
        if (onLikeChange) {
          onLikeChange();
        }
      }
    } catch (error) {
      console.error("Error toggling like:", error);
    }
  }

  function handleUserClick(e: React.MouseEvent, userId: string) {
    e.stopPropagation();
    setLocation(`/users/${userId}`);
  }

  async function handleShopClick(e: React.MouseEvent, shopId: string) {
    e.stopPropagation();
    // Try to get shop with full details for SEO URL
    try {
      const response = await fetch(`/api/shops/${shopId}`);
      if (response.ok) {
        const shop = await response.json();
        setLocation(getShopProfileUrl(shop, locale));
      } else {
        setLocation(`/shops/${shopId}`);
      }
    } catch (error) {
      setLocation(`/shops/${shopId}`);
    }
  }

  async function handleRouteClick(e: React.MouseEvent, routeId: string) {
    e.stopPropagation();
    // Try to get route slug first
    try {
      const response = await fetch(`/api/routes/${routeId}`);
      if (response.ok) {
        const route = await response.json();
        if (route.slug) {
          setLocation(`/kringlooproutes/${route.slug}`);
        } else if (route.name) {
          setLocation(`/kringlooproutes/${generateRouteSlug(route.name)}`);
        } else {
          setLocation(`/routes/${routeId}`);
        }
      } else {
        setLocation(`/routes/${routeId}`);
      }
    } catch (error) {
      setLocation(`/routes/${routeId}`);
    }
  }

  return (
    <>
      <Card
        className="overflow-hidden hover-elevate cursor-pointer"
        data-testid={`card-post-${post.id}`}
        onClick={handleImageClick}
      >
        <div className="aspect-square relative overflow-hidden bg-muted">
          {images.length === 0 ? (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">{t("postCard.noImage")}</div>
          ) : images.length === 1 ? (
            <img
              src={images[0]}
              alt={caption || ""}
              className="w-full h-full object-contain transition-transform hover:scale-105 duration-500"
              loading="lazy"
            />
          ) : (
            <div className={`grid h-full w-full gap-0.5 ${images.length === 2 ? 'grid-cols-2' : 'grid-cols-2 grid-rows-2'}`}>
              {images.slice(0, 4).map((src, i) => (
                <div
                  key={i}
                  className={`relative overflow-hidden bg-secondary ${images.length === 3 && i === 0 ? 'row-span-2' : ''
                    }`}
                >
                  <img src={src} className="w-full h-full object-contain hover:scale-110 transition-transform duration-500" loading="lazy" alt="" />
                </div>
              ))}
            </div>
          )}

          {category && (
            <Badge
              variant="secondary"
              className="absolute top-2 right-2 text-xs shadow-sm"
            >
              {category}
            </Badge>
          )}
        </div>

        <CardContent className="p-3 space-y-2">
          <div className="flex items-center gap-2">
            <Avatar className="h-6 w-6">
              {displayAuthorAvatar ? (
                <img src={displayAuthorAvatar} alt={displayAuthorName} className="h-full w-full object-cover rounded-full" />
              ) : (
                <AvatarFallback className="text-xs bg-primary/10 text-primary">
                  {displayAuthorName?.[0].toUpperCase() || 'U'}
                </AvatarFallback>
              )}
            </Avatar>
            <div className="flex items-center gap-1 min-w-0 flex-1">
              <button
                onClick={(e) => handleUserClick(e, post.authorId || post.author?.id)}
                className="text-sm font-medium truncate hover:underline"
              >
                {displayAuthorName}
              </button>
              {isOwnerPost && (
                <Crown className="h-3.5 w-3.5 text-yellow-500 flex-shrink-0" data-testid={`badge-owner-${post.id}`} />
              )}
              {/* TODO: Add badge icons here */}
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="text-muted-foreground hover:text-foreground p-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  className="font-medium"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isLoggedIn) {
                      setShowLoginModal(true);
                      return;
                    }
                    setWishlistModalOpen(true);
                  }}
                >
                  {t("nav.wishlist")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isLoggedIn) {
                      setShowLoginModal(true);
                      return;
                    }
                    setShowReportModal(true);
                  }}
                >
                  <Flag className="h-4 w-4 mr-2" />
                  {t("postCard.report")}
                </DropdownMenuItem>

                {user?.id === post.authorId && (
                  <>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowEditModal(true);
                      }}
                    >
                      <Edit2 className="h-4 w-4 mr-2" />
                      {t("common.edit")}
                    </DropdownMenuItem>

                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (confirm(t("postCard.confirmDelete"))) {
                          try {
                            const authHeader = await getAuthHeader();
                            const response = await fetch(`/api/posts/${post.id}`, {
                              method: "DELETE",
                              headers: {
                                ...(authHeader || {}),
                              },
                            });

                            if (response.ok) {
                              if (onLikeChange) { // Reuse onLikeChange or rename it to onUpdate
                                onLikeChange();
                              }
                            }
                          } catch (error) {
                            console.error("Error deleting post:", error);
                          }
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {t("common.delete")}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div data-testid={`text-caption-${post.id}`}>
            <TranslatableText
              table={post.type === 'shop_post' ? "shop_posts" : "gems"}
              rowId={post.id}
              text={caption || ""}
              translations={(post as any).translations}
              className="text-sm line-clamp-2"
            />
          </div>

          {post.shopId && (
            <button
              onClick={(e) => handleShopClick(e, post.shopId!)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:underline w-full"
            >
              <Store className="h-3 w-3" />
              <span className="truncate">
                {(post as any).shopName || t("shops.title")}
                {(post as any).shopCity && <span className="text-muted-foreground ml-1">• {(post as any).shopCity}</span>}
              </span>
            </button>
          )}
          {post.routeId && (
            <button
              onClick={(e) => handleRouteClick(e, post.routeId!)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:underline w-full"
            >
              <Car className="h-3 w-3" />
              <span className="truncate">{t("shopDetail.route")}</span>
            </button>
          )}

          <div className="flex items-center justify-between pt-2 border-t border-border">
            <div className="flex items-center gap-3 text-xs">
              <button
                onClick={handleLike}
                className={`flex items-center gap-1 ${liked ? "text-primary" : "text-muted-foreground hover:text-primary"}`}
              >
                <Heart className={`h-3.5 w-3.5 ${liked ? "fill-current" : ""}`} />
                {likeCount}
              </button>
              <button
                onClick={handleCommentClick}
                className={`flex items-center gap-1 ${commentsCount > 0 ? "text-primary" : "text-muted-foreground hover:text-primary"}`}
              >
                <MessageCircle className={`h-3.5 w-3.5 ${commentsCount > 0 ? "fill-current opacity-20" : ""}`} />
                {commentsCount}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setShareModalOpen(true); }}
                className="flex items-center gap-1 text-muted-foreground hover:text-primary"
              >
                <Share2 className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="flex items-center gap-2">
              {(post as any).pricePaid !== null && (post as any).pricePaid !== undefined && (
                <span className="text-xs font-semibold px-1.5 py-0.5 bg-primary/10 text-primary rounded">
                  {new Intl.NumberFormat(locale === "nl" ? "nl-NL" : "en-US", {
                    style: "currency",
                    currency: (post as any).currency || "EUR",
                  }).format((post as any).pricePaid / 100)}
                </span>
              )}
              <span className="text-xs text-muted-foreground">
                {new Date(createdAt).toLocaleDateString(locale === "nl" ? "nl-NL" : locale === "en-gb" ? "en-GB" : `${locale}-${locale.toUpperCase()}`, { day: "numeric", month: "short" })}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      <PostDetailModal
        post={post}
        open={showDetail}
        onOpenChange={setShowDetail}
        onLikeChange={() => {
          if (onLikeChange) {
            onLikeChange();
          }
        }}
      />

      <LoginModal
        open={showLoginModal}
        onOpenChange={setShowLoginModal}
        action="post"
      />

      <ReportModal
        open={showReportModal}
        onOpenChange={setShowReportModal}
        postId={post.id}
      />

      <PostComposerModal
        open={showEditModal}
        onOpenChange={setShowEditModal}
        postId={post.id}
        initialCaption={caption}
        initialCategory={category}
        initialImages={images}
        onSuccess={() => {
          if (onLikeChange) onLikeChange();
        }}
      />

      <ShareModal
        open={shareModalOpen}
        onOpenChange={setShareModalOpen}
        title={t("gems.shareTitle")}
        text={t("gems.shareText", { caption: post.caption })}
        url={`${window.location.origin}/community?post=${post.id}`}
        imageUrl={(post as any).share_image_url || mainImage || post.imageUrl || undefined}
        entityType="post"
        entityId={post.id}
      />

      <WishlistItemFormDialog
        open={wishlistModalOpen}
        onClose={() => setWishlistModalOpen(false)}
        initialData={{
          item_name: category || "",
          image_url: mainImage || post.imageUrl || undefined,
          image_thumbnail_url: mainImage || post.imageUrl || undefined,
          notes: caption || undefined,
        }}
        onSubmit={(data) => addWishlistMutation.mutate(data)}
        isLoading={addWishlistMutation.isPending}
        uploadImage={uploadImage}
      />
    </>
  );
}
